import { Injectable } from '@angular/core';
import { SessionService } from './session.service';
import { from, Observable, of, zip } from 'rxjs';
import { importJWK } from 'jose';
import { map, mergeMap } from 'rxjs/operators';

const ENCRYPT_ALGO: RsaOaepParams = { name: 'RSA-OAEP' };

@Injectable({
  providedIn: 'root',
})
export class EncryptionService {
  constructor(private sessionService: SessionService) {}

  private static arrayBufferToBase64(arrayBuffer: ArrayBuffer): string {
    let stringRepresentation = '';
    const view = new Uint8Array(arrayBuffer);
    const length = view.byteLength;

    for (let i = 0; i < length; i++) {
      stringRepresentation += String.fromCharCode(view[i]);
    }

    return btoa(stringRepresentation);
  }

  /**
   * Implementation note, hard-coded to encode in UTF-16
   *
   * @param str string to encode
   * @return ArrayBuffer encoded array buffer
   * @private
   */
  private static stringToArrayBuffer(str: string): ArrayBuffer {
    const stringLength = str.length;

    const buffer = new ArrayBuffer(stringLength * 2); // UTF-16 defines 2 bytes per character
    const bufferView = new Uint16Array(buffer);

    for (let i = 0; i < stringLength; i++) {
      bufferView[i] = str.charCodeAt(i);
    }

    return buffer;
  }

  public encrypt(payload: string): Observable<string[] | undefined> {
    const session = this.sessionService.getCurrentSession();

    if (!session) return of(undefined);

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { oth, key_ops, ...pubKey } = session.publicKey;
    const key = from(importJWK(pubKey, 'RSA-OAEP-512'));

    const payloadBuffer = EncryptionService.stringToArrayBuffer(payload);

    // RSA-OAEP only supports ciphertext payloads of 126 bytes or less
    const chunks = Math.ceil(payloadBuffer.byteLength / 126);
    const encryptionArray = [];

    for (let i = 0; i < chunks; i++) {
      encryptionArray.push(
        key.pipe(
          mergeMap((encryptKey) =>
            crypto.subtle.encrypt(
              ENCRYPT_ALGO,
              encryptKey as CryptoKey,
              payloadBuffer.slice(126 * i, 126 * (i + 1)),
            ),
          ),
          map(EncryptionService.arrayBufferToBase64),
        ),
      );
    }

    return zip(...encryptionArray);
  }
}
